import type { ApiError } from "../../api/client";
import type { CmsPage } from "../../types/cms";
import { FRAGMENT_PAGE_TYPE } from "./pageTypeRegistry";
import { type PageSection, pageSectionOf } from "./pageSection";

export const SEGMENT_REQUIRED_MESSAGE = "Enter a slug for the page address.";

export const SEGMENT_RULE_MESSAGE =
  "Use only lowercase letters, numbers and hyphens, e.g. cookies-policy.";

export const SEGMENT_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const SEGMENT_MAX_LENGTH = 200;

export type SlugContext = {
  section: PageSection;
  pages: CmsPage[] | null;
  editedPageId: string | null;
  duplicating?: boolean;
};

export function nestedSlugMessage(value: string, context: SlugContext): string | null {
  if (!value.includes("/")) {
    return null;
  }
  const parts = value
    .trim()
    .split("/")
    .map(part => part.trim())
    .filter(Boolean);
  const segment = parts.at(-1);
  if (!segment) {
    return "Slugs can't contain /. Enter just this page's part of the address, e.g. cookies-policy.";
  }
  if (parts.length === 1) {
    return `Slugs can't contain /. Use “${segment}” as the slug.`;
  }
  const parentPath = parts.slice(0, -1).join("/");
  const target = sectionPath(context.section, parts.join("/"));
  const parentAddress = sectionPath(context.section, parentPath);
  const parent = context.pages
    ? parentPageChoices(context.pages, context.editedPageId, context.section).find(
        page => page.slug === parentPath,
      )
    : undefined;
  const prefix = `Slugs can't contain /. To publish at ${target},`;
  if (parent) {
    return context.duplicating
      ? `${prefix} use “${segment}” here, then choose “${parent.title}” (${parentAddress}) as the copy's Parent page.`
      : `${prefix} choose “${parent.title}” (${parentAddress}) as the Parent page and use “${segment}” as the slug.`;
  }
  if (context.pages) {
    return context.duplicating
      ? `${prefix} first create a page at ${parentAddress} (it can stay a draft), then use “${segment}” here and choose that page as the copy's Parent page.`
      : `${prefix} first create a page at ${parentAddress} (it can stay a draft), then choose it as the Parent page and use “${segment}” as the slug.`;
  }
  return context.duplicating
    ? `${prefix} use “${segment}” here, then choose the page at ${parentAddress} as the copy's Parent page (create it first if it doesn't exist; it can stay a draft).`
    : `${prefix} choose the page at ${parentAddress} as the Parent page (create it first if it doesn't exist; it can stay a draft) and use “${segment}” as the slug.`;
}

export function validateSegment(value: string, context?: SlugContext): true | string {
  const segment = value.trim();
  if (!segment) {
    return SEGMENT_REQUIRED_MESSAGE;
  }
  const nested = context ? nestedSlugMessage(segment, context) : null;
  if (nested) {
    return nested;
  }
  if (segment.length > SEGMENT_MAX_LENGTH) {
    return `Keep the slug to ${SEGMENT_MAX_LENGTH} characters or fewer.`;
  }
  if (!SEGMENT_PATTERN.test(segment)) {
    return SEGMENT_RULE_MESSAGE;
  }
  return true;
}

const PUBLIC_HOST = "www.legal500.com";

export function isSectionHome(page: { sectionHome?: boolean; slug: string }): boolean {
  return page.sectionHome ?? page.slug === "";
}

export function sectionHomeOf(
  pages: CmsPage[],
  section: PageSection,
  editedPageId: string | null,
): CmsPage | null {
  return (
    pages.find(
      page => page.id !== editedPageId && pageSectionOf(page) === section && isSectionHome(page),
    ) ?? null
  );
}

export function joinPath(parentPath: string | null, segment: string): string {
  return parentPath && segment ? `${parentPath}/${segment}` : parentPath || segment;
}

export function sectionPath(section: PageSection, fullSlug: string): string {
  return fullSlug ? `/${section}/${fullSlug}` : `/${section}`;
}

export function pagePath(page: Pick<CmsPage, "slug" | "section">): string {
  return sectionPath(pageSectionOf(page), page.slug);
}

export function publicPageAddress(
  section: PageSection,
  parentSlug: string | null,
  segment: string,
): string {
  return `${PUBLIC_HOST}${sectionPath(section, joinPath(parentSlug, segment))}`;
}

export const SLUG_TAKEN_MESSAGE =
  "Another page already uses this address. Choose a different slug.";

export function isSlugConflict(err: unknown): boolean {
  return err instanceof Error && (err as ApiError).status === 409;
}

export function parentPathOf(page: CmsPage): string | null {
  if (!page.parentId) {
    return null;
  }
  const parts = page.slug.split("/");
  return parts.length > 1 ? parts.slice(0, -1).join("/") : null;
}

export function descendantPages(pages: CmsPage[], pageId: string): CmsPage[] {
  const ids = withDescendantIds(pages, pageId);
  return pages
    .filter(page => page.id !== pageId && ids.has(page.id))
    .sort((a, b) => a.slug.localeCompare(b.slug));
}

export function movedPath(path: string, oldPrefix: string, newPrefix: string): string {
  return joinPath(newPrefix, path.slice(oldPrefix.length).replace(/^\//, ""));
}

function withDescendantIds(pages: CmsPage[], pageId: string): Set<string> {
  const excluded = new Set([pageId]);
  let added = true;
  while (added) {
    added = false;
    for (const page of pages) {
      if (page.parentId && excluded.has(page.parentId) && !excluded.has(page.id)) {
        excluded.add(page.id);
        added = true;
      }
    }
  }
  return excluded;
}

export function parentPageChoices(
  pages: CmsPage[],
  editedPageId: string | null,
  section: PageSection,
): CmsPage[] {
  const excluded = editedPageId ? withDescendantIds(pages, editedPageId) : new Set<string>();
  return pages
    .filter(
      page =>
        page.type !== FRAGMENT_PAGE_TYPE &&
        !excluded.has(page.id) &&
        pageSectionOf(page) === section,
    )
    .sort((a, b) => a.slug.localeCompare(b.slug));
}

export const SEGMENT_HINT = "Lowercase letters, numbers and hyphens only.";

export function slugDescribedBy(slugId: string, hasError: boolean): string {
  const ids = [`${slugId}-hint`, `${slugId}-address`];
  return hasError ? [`${slugId}-error`, ...ids].join(" ") : ids.join(" ");
}
